/**
 * Cart Drawer - Complete Implementation
 * Handles: open/close drawer, add to cart, render updates
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
  }

  connectedCallback() {
    // Escape key closes drawer
    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.close();
    });
    
    // Close buttons - use event delegation for dynamically added buttons
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]') || e.target.id === 'CartDrawer-Overlay') {
        e.preventDefault();
        this.close();
      }
    });
    
    // Setup cart icon click
    this.bindCartIcon();
  }

  bindCartIcon() {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon && !cartIcon._drawerBound) {
      cartIcon._drawerBound = true;
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(cartIcon);
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    
    this.setAttribute('open', '');
    this.classList.add('is-open', 'active');
    document.body.classList.add('overflow-hidden');
    
    setTimeout(() => {
      const closeBtn = this.querySelector('.drawer__close');
      if (closeBtn) closeBtn.focus();
      
      if (typeof trapFocus === 'function') {
        const container = this.classList.contains('is-empty') 
          ? this.querySelector('.drawer__inner-empty') 
          : this.querySelector('#CartDrawer');
        if (container) trapFocus(container, closeBtn);
      }
    }, 50);
  }

  close() {
    this.removeAttribute('open');
    this.classList.remove('is-open', 'active', 'animate');
    document.body.classList.remove('overflow-hidden');
    
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus();
    }
  }

  // Required by product-form.js
  setActiveElement(element) {
    this.activeElement = element;
  }

  // Required by product-form.js - returns section IDs for Shopify API
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' }
    ];
  }

  // Required by product-form.js - renders the response after add to cart
  renderContents(parsedState) {
    if (!parsedState) {
      console.warn('CartDrawer: No parsed state to render');
      return;
    }
    
    const sections = parsedState.sections || parsedState;
    
    // Update cart drawer
    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(drawerHTML, 'text/html');
      const newDrawer = doc.querySelector('#CartDrawer');
      const currentDrawer = document.querySelector('#CartDrawer');
      
      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }
    }
    
    // Update cart icon bubble
    const iconHTML = sections['cart-icon-bubble'];
    if (iconHTML) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(iconHTML, 'text/html');
      const newIcon = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
      const currentIcon = document.querySelector('#cart-icon-bubble');
      
      if (newIcon && currentIcon) {
        currentIcon.innerHTML = newIcon.innerHTML;
      }
    }

    // Update state and open
    this.classList.remove('is-empty');
    this.open();
    
    // Re-bind cart icon in case it was replaced
    this.bindCartIcon();
  }
}

// Register the custom element
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
